/**
 * Vertex handle for editing individual polyline vertices.
 *
 * Performance optimization: Uses the ODA view.add() pattern.
 * Ghost entities are added directly to the view (not database) for fast
 * interactive rendering during drag operations.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, Point3D, HandleDrawOptions } from "../types";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
} from "../types";
import { distance2D } from "../matrix";
import { deleteObj } from "../../core/delete";
import { openAs } from "../../core/oda";
import { MovePolylineVertexCommand } from "../../commands/MovePolylineVertexCommand";
import { MeasurementLabels } from "../../overlay/MeasurementLabels";
import { Facade } from "../../Facade";

import { AbstractHandle } from "./AbstractHandle";
import { createViewGhost } from "../../entities/ghost";

/**
 * Handle for moving a single vertex of a polyline.
 */
export class VertexHandle extends AbstractHandle {
  readonly type = HandleType.Vertex;
  readonly position = HandlePosition.Center; // Not really used for vertex handles

  protected entityId: DrawingModule.OdDbObjectId;
  protected vertexIndex: number;

  /** Draw options */
  drawOptions: HandleDrawOptions = {
    fillColor: TRANSFORM_THEME.vertexHandle,
    strokeColor: TRANSFORM_THEME.handleStroke,
    radius: HANDLE_SIZES.vertex * HANDLE_SIZES.handleScale,
  };

  /** Original vertex position for undo/cancel */
  protected originalPoint: { x: number; y: number } | null = null;

  /** Entity handle string */
  protected entityHandle: string = "";

  private measureLabels: MeasurementLabels | null = null;

  /** Previous vertex world position (for angle indicator relative to segment) */
  private previousVertexWorld: { x: number; y: number } | null = null;

  /** Ghost entity for view-based rendering (performance optimization) */
  private ghostEntity: DrawingModule.OdDbEntity | null = null;

  /** Original anti-aliasing mode to restore after drag */
  private originalAAMode: number = 0;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityId: DrawingModule.OdDbObjectId,
    vertexIndex: number,
    initialPosition: Point3D,
  ) {
    super(viewer, history);
    this.entityId = entityId;
    this.vertexIndex = vertexIndex;
    this.worldPosition = { ...initialPosition };

    // Store entity handle string
    this.entityHandle = this.getEntityHandle(entityId);
  }

  /**
   * Hit test - check if point is within handle radius.
   */
  hitTest(worldPoint: Point3D, tolerance: number): boolean {
    const dist = distance2D(worldPoint, this.worldPosition);
    return dist <= tolerance;
  }

  /**
   * Start vertex move - store original position and create ghost.
   */
  start(): void {
    if (this.isTransforming) return;
    this.isTransforming = true;

    // Store original vertex position
    this.originalPoint = this.getVertexPosition();

    // Store previous vertex position for angle indicator
    this.previousVertexWorld = this.getPreviousVertexPosition();

    // Exclude this entity from snap to prevent self-snapping
    if (this.entityHandle) {
      Facade.snap.setExcludedHandles([this.entityHandle]);
    }

    // Create ghost entity for view-based rendering (performance optimization)
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);
    if (view) {
      // PERFORMANCE: Store original AA mode and disable it for faster rendering
      this.originalAAMode = view.antiAliasingMode();
      view.enableAntiAliasing(0);

      // Try interactive mode (may not work in all WebAssembly builds)
      try {
        if (!view.isInInteractivity()) {
          view.beginInteractivity(60);
        }
      } catch {
        // beginInteractivity not supported
      }

      // Ghost only: the original stays in the view underneath, as it does for
      // every other handle. Erasing it here and re-adding it in end()/cancel()
      // left the entity unrendered — view.add() hands ODA a pointer that
      // deleteObj() then destroys on the next line, before any App.Update().
      const ghost = createViewGhost(this.module, this.viewer, this.entityId);
      if (ghost) {
        this.ghostEntity = ghost;
        view.add(ghost, null as unknown as DrawingModule.OdGsModel);
      }
    }
  }

  /**
   * Move vertex during drag.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming) return;

    // Move the vertex on ghost entity (fast) or original (fallback)
    if (this.ghostEntity) {
      const ghostPolyline = this.module.OdDbPolyline.cast(this.ghostEntity);
      if (ghostPolyline) {
        const point2d = new this.module.OdGePoint2d(
          context.worldPoint.x,
          context.worldPoint.y,
        );
        ghostPolyline.setPointAt(this.vertexIndex, point2d);
        deleteObj(point2d);
        // No recordGraphicsModified needed for ghosts - fast!
      }
    } else {
      // Fallback: modify original (slow path)
      this.setVertexPosition(context.worldPoint.x, context.worldPoint.y);
    }

    // Update handle position
    this.worldPosition = { ...context.worldPoint };

    // Update view with throttling for better performance
    this.viewer.throttledUpdate();

    this.updateMeasurements(context);
  }

  /**
   * End vertex move - execute command internally.
   */
  end(context: TransformContext): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
    Facade.snap.clearExcludedHandles();

    if (!this.isTransforming || !this.originalPoint) return;

    this.isTransforming = false;

    const newPoint = { x: context.worldPoint.x, y: context.worldPoint.y };
    const oldPoint = this.originalPoint;

    // Clean up ghost and restore original to view
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // PERFORMANCE: Restore AA mode and end interactive mode
    if (view) {
      try {
        if (view.isInInteractivity()) {
          view.endInteractivity();
        }
      } catch {
        // endInteractivity not supported
      }
      view.enableAntiAliasing(this.originalAAMode);
    }

    const ghostToDelete = this.ghostEntity;
    this.ghostEntity = null;
    if (ghostToDelete && view) {
      view.erase(ghostToDelete);
    }

    // Check if vertex actually moved
    const dx = Math.abs(newPoint.x - oldPoint.x);
    const dy = Math.abs(newPoint.y - oldPoint.y);
    const threshold = 0.001; // Small threshold for floating point comparison

    if (dx < threshold && dy < threshold) {
      // No movement - don't create command
      this.originalPoint = null;
      this.previousVertexWorld = null;
      try {
        this.safeViewerUpdate("VertexHandle.end/no-move", {
          entityHandle: this.entityHandle,
        });
      } finally {
        if (ghostToDelete) deleteObj(ghostToDelete);
      }
      return;
    }

    // Create and execute command (this will apply the change to the original)
    const command = new MovePolylineVertexCommand(this.viewer, {
      handle: this.entityHandle,
      vertices: [
        {
          vertexIndex: this.vertexIndex,
          oldPoint: oldPoint,
          newPoint: newPoint,
        },
      ],
    });
    this.history.execute(command);

    this.originalPoint = null;
    this.previousVertexWorld = null;
    try {
      this.safeViewerUpdate("VertexHandle.end", {
        entityHandle: this.entityHandle,
      });
    } finally {
      if (ghostToDelete) deleteObj(ghostToDelete);
    }
  }

  /**
   * Cancel vertex move - restore original position.
   */
  cancel(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
    Facade.snap.clearExcludedHandles();

    if (!this.isTransforming) return;

    // Clean up ghost and restore original to view (original was never modified)
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);

    // PERFORMANCE: Restore AA mode and end interactive mode
    if (view) {
      try {
        if (view.isInInteractivity()) {
          view.endInteractivity();
        }
      } catch {
        // endInteractivity not supported
      }
      view.enableAntiAliasing(this.originalAAMode);
    }

    const ghostToDelete = this.ghostEntity;
    this.ghostEntity = null;
    if (ghostToDelete && view) {
      view.erase(ghostToDelete);
    } else if (this.originalPoint) {
      // Fallback: restore original vertex position (only if no ghost was used)
      this.setVertexPosition(this.originalPoint.x, this.originalPoint.y);
    }

    // Restore handle position
    if (this.originalPoint) {
      this.worldPosition = {
        x: this.originalPoint.x,
        y: this.originalPoint.y,
        z: 0,
      };
    }

    this.isTransforming = false;
    this.originalPoint = null;
    this.previousVertexWorld = null;

    try {
      this.safeViewerUpdate("VertexHandle.cancel", {
        entityHandle: this.entityHandle,
      });
    } finally {
      if (ghostToDelete) deleteObj(ghostToDelete);
    }
  }

  // ============== Measurements ==============

  private updateMeasurements(context: TransformContext): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || !this.originalPoint) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }

    // World displacement
    const wdx = context.worldPoint.x - this.originalPoint.x;
    const wdy = context.worldPoint.y - this.originalPoint.y;
    const worldDist = Math.sqrt(wdx * wdx + wdy * wdy);

    if (worldDist <= 0.0001) {
      this.measureLabels.clear();
      return;
    }

    // Angle indicator from previous vertex to current position
    this.updateAngleIndicator(context);
  }

  private updateAngleIndicator(context: TransformContext): void {
    if (!this.measureLabels || !this.previousVertexWorld) return;

    const prevScreen = this.viewer.worldToScreen(this.previousVertexWorld);

    const segWdx = context.worldPoint.x - this.previousVertexWorld.x;
    const segWdy = context.worldPoint.y - this.previousVertexWorld.y;
    const segDist = Math.sqrt(segWdx * segWdx + segWdy * segWdy);

    if (segDist <= 0.0001) {
      this.clearAngleIndicator();
      return;
    }

    const worldAngle = Math.atan2(segWdy, segWdx);
    // Negate for screen (Y-up world → Y-down screen)
    const screenAngle = -worldAngle;
    const ARC_RADIUS = 30;
    const lineLen = ARC_RADIUS * 1.5;

    // Reference line along screen x-axis from previous vertex
    this.measureLabels.setLine(
      "refLine",
      prevScreen.x,
      prevScreen.y,
      prevScreen.x + lineLen,
      prevScreen.y,
    );

    // Direction line from previous vertex toward current position
    this.measureLabels.setLine(
      "dirLine",
      prevScreen.x,
      prevScreen.y,
      prevScreen.x + Math.cos(screenAngle) * lineLen,
      prevScreen.y + Math.sin(screenAngle) * lineLen,
    );

    // Arc from x-axis (0) to the screen angle
    this.measureLabels.setArc(
      "arc",
      prevScreen.x,
      prevScreen.y,
      ARC_RADIUS,
      0,
      screenAngle,
      worldAngle > 0,
    );

    // Angle label at arc midpoint
    const midScreenAngle = screenAngle / 2;
    const labelDist = ARC_RADIUS + 16;
    let degrees = worldAngle * (180 / Math.PI);
    degrees = parseFloat(degrees.toFixed(1));
    this.measureLabels.setLabel(
      "angle",
      prevScreen.x + Math.cos(midScreenAngle) * labelDist,
      prevScreen.y + Math.sin(midScreenAngle) * labelDist,
      MeasurementLabels.formatAngle(degrees),
    );
  }

  private clearAngleIndicator(): void {
    if (!this.measureLabels) return;
    this.measureLabels.removeLabel("angle");
    this.measureLabels.removeGuide("refLine");
    this.measureLabels.removeGuide("dirLine");
    this.measureLabels.removeGuide("arc");
  }

  // ============== Private Methods ==============

  /**
   * Get previous vertex position from polyline (handles closed wrapping).
   * Returns null if this is the first vertex of an open polyline.
   */
  private getPreviousVertexPosition(): { x: number; y: number } | null {
    const polyline = openAs(this.entityId, this.module.OdDbPolyline, 0);
    const numVerts = polyline.numVerts();

    let prevIndex = this.vertexIndex - 1;
    if (prevIndex < 0) {
      if (polyline.isClosed()) {
        prevIndex = numVerts - 1;
      } else {
        deleteObj(polyline);
        return null;
      }
    }

    const point2d = new this.module.OdGePoint2d(0, 0);
    polyline.getPointAt(prevIndex, point2d);
    const result = { x: point2d.x, y: point2d.y };
    deleteObj(point2d);
    deleteObj(polyline);
    return result;
  }

  /**
   * Get current vertex position from polyline.
   */
  private getVertexPosition(): { x: number; y: number } | null {
    const polyline = openAs(this.entityId, this.module.OdDbPolyline, 0);
    const point2d = new this.module.OdGePoint2d(0, 0);
    polyline.getPointAt(this.vertexIndex, point2d);
    const result = { x: point2d.x, y: point2d.y };
    deleteObj(point2d);
    deleteObj(polyline);
    return result;
  }

  /**
   * Set vertex position on polyline.
   */
  private setVertexPosition(x: number, y: number): void {
    const polyline = openAs(this.entityId, this.module.OdDbPolyline, 1);
    const point2d = new this.module.OdGePoint2d(x, y);
    polyline.setPointAt(this.vertexIndex, point2d);
    deleteObj(point2d);

    // Mark entity as graphically modified
    polyline.recordGraphicsModified(true);
    deleteObj(polyline);
  }

  /**
   * Get entity handle string.
   */
  getEntityHandle(entityId: DrawingModule.OdDbObjectId): string {
    const handle = entityId.getHandle();
    const handleStr = handle.ascii();
    const result = handleStr.toString();
    deleteObj(handleStr);
    deleteObj(handle);
    return result;
  }
}
